import { useEffect, useState } from "react";
import { Alert, Button, CircularProgress, Typography } from "@mui/material";
import { Package, Users } from "lucide-react";
import { ServerConfig, fullHttpURL } from "../serverConfig";
import { LiveOrder } from "./LiveOrders";

type InventoryItem = { skuId: number; description: string; price: number; quantity: number; itemsLeft: number; buyList: string[]; waitList: string[] };

export function ProductsPanel({ serverConfig, openSetup }: { serverConfig: ServerConfig; openSetup: () => void }) {
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    const refresh = async () => {
      try {
        const response = await fetch(fullHttpURL(serverConfig) + "/sales");
        if (!response.ok) throw new Error(`Không tải được sản phẩm (HTTP ${response.status})`);
        const value = await response.json() as InventoryItem[];
        if (alive) { setItems(value); setError(null); }
      } catch (reason) { if (alive) setError(reason instanceof Error ? reason.message : "Không tải được sản phẩm"); }
      finally { if (alive) setLoading(false); }
    };
    void refresh(); const timer = window.setInterval(refresh, 5000);
    return () => { alive = false; window.clearInterval(timer); };
  }, [serverConfig]);
  return <section className="mf-data-panel"><div className="mf-panel-heading"><div><h2>Sản phẩm & tồn kho</h2><p>Tồn kho của phiên live hiện tại, cập nhật tự động.</p></div><Button variant="outlined" onClick={openSetup}>Thiết lập bán hàng</Button></div>
    {error && <Alert severity="error">{error}</Alert>}{loading ? <CircularProgress size={22}/> : items.length === 0 ? <div className="mf-panel-empty"><Package size={38}/><h3>Chưa có sản phẩm trong phiên live</h3><p>Vào thiết lập bán hàng để đặt số mặt hàng trước khi bắt đầu theo dõi live.</p></div> : <div className="mf-panel-table"><div className="head"><span>Mã</span><span>Sản phẩm</span><span>Ban đầu</span><span>Còn lại</span><span>Đã đặt</span></div>{items.map(item => <div key={item.skuId}><strong>#{item.skuId}</strong><span>{item.description || `Mặt hàng ${item.skuId}`}</span><span>{item.quantity}</span><strong>{item.itemsLeft}</strong><span>{item.buyList.length}</span></div>)}</div>}
    <Typography variant="body2" sx={{ mt: 2 }}>Thông tin tên và giá sản phẩm chưa có chức năng chỉnh sửa trong phiên bản này; hiện hệ thống dùng số thứ tự mặt hàng để xử lý bình luận.</Typography>
  </section>;
}

export function CustomersPanel({ serverConfig }: { serverConfig: ServerConfig }) {
  const [orders, setOrders] = useState<LiveOrder[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    const refresh = async () => {
      try {
        const response = await fetch(fullHttpURL(serverConfig) + "/orders/live");
        if (!response.ok) throw new Error(`Không tải được khách hàng (HTTP ${response.status})`);
        const value = await response.json() as LiveOrder[];
        if (alive) { setOrders(value); setError(null); }
      } catch (reason) { if (alive) setError(reason instanceof Error ? reason.message : "Không tải được khách hàng"); }
      finally { if (alive) setLoading(false); }
    };
    void refresh(); const timer = window.setInterval(refresh, 5000);
    window.addEventListener("live-orders-changed", refresh);
    return () => { alive = false; window.clearInterval(timer); window.removeEventListener("live-orders-changed", refresh); };
  }, [serverConfig]);
  const byCustomer = new Map<string, { name: string; profileUrl: string; count: number; latest: string }>();
  for (const order of orders) {
    const key = order.profileUrl || order.authorName;
    const old = byCustomer.get(key);
    byCustomer.set(key, { name: order.authorName, profileUrl: order.profileUrl, count: (old?.count ?? 0) + 1, latest: order.status });
  }
  return <section className="mf-data-panel"><div className="mf-panel-heading"><div><h2>Khách hàng</h2><p>Khách đã có đơn live của shop này.</p></div></div>
    {error && <Alert severity="error">{error}</Alert>}{loading ? <CircularProgress size={22}/> : byCustomer.size === 0 ? <div className="mf-panel-empty"><Users size={38}/><h3>Chưa có khách hàng</h3><p>Khách sẽ xuất hiện ở đây sau khi có đơn nháp từ bình luận live.</p></div> : <div className="mf-customer-list">{[...byCustomer.values()].map(customer => <div key={customer.profileUrl || customer.name}><span className="avatar">{customer.name.charAt(0).toUpperCase()}</span><div><strong>{customer.name}</strong><small>{customer.count} đơn · Gần nhất: {customer.latest}</small></div>{customer.profileUrl && <a href={customer.profileUrl} target="_blank" rel="noopener noreferrer">Mở Facebook</a>}</div>)}</div>}
    <Typography variant="body2" sx={{ mt: 2 }}>Danh sách này chỉ gồm người đã tạo đơn. Tên tác giả bình luận có thể thiếu nếu Facebook chưa cấp dữ liệu đó.</Typography>
  </section>;
}
